import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import { toast } from "react-toastify";
import { authFetch } from "../utils/authFetch";
import { BASE_URL } from "../utils/config";
import { ReactComponent as DocumentIconComponent } from "../images/pdf.svg"; //done
import { ReactComponent as GlobalIconComponent } from "../images/global.svg"; //done
import { ReactComponent as VirtualTourComponent } from "../images/vtour.svg"; // done
import { useFlow } from "../context/FlowContext";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "react-i18next";
import styles from "./EditOrView.module.css";
import NewOffersSwiper from "../components/NewOffersSwiper";
import NewSearchesSwiper from "../components/NewSearchesSwiper";
import PropertyImageUploadModal from "../components/PropertyImageUploadModal";
import ChevronLeftIcon from "../images/chevron-left-outlined-rounded.svg";
import ChevronRightIcon from "../images/chevron-right-outlined-rounded.svg";

const EditOrView = () => {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const location = useLocation();
  const { fromDiscoverMore, fromMyMatchingsTable, fromBookmarksPage, fromNewOffers } = location.state || {};
  const navigate = useNavigate();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeThumb, setActiveThumb] = useState(0);
  const [allPropertyIds, setAllPropertyIds] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(-1);
  const { currentFlow } = useFlow();
  const { currentUser, isAuthenticated, loadingAuth } = useAuth();
  const [showEditButton, setShowEditButton] = useState(true);
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [displayedOffers, setDisplayedOffers] = useState([]);
  // near your other useState hooks
  const [demandSliderVersion, setDemandSliderVersion] = useState(0);
  const [offerSliderVersion, setOfferSliderVersion] = useState(0);
  const [criteriaGroups, setCriteriaGroups] = useState([]);

  const [vendorDetails, setVendorDetails] = useState({
    name: "",
    email: "",
    phone: "",
  });
  const [showImageUploadModal, setShowImageUploadModal] = useState(false);

  // Helper function to get the appropriate room/unit label based on property type
  const getRoomLabel = (propertyType) => {
    if (!propertyType) return t("bedrooms");
    const type = propertyType.toLowerCase();
    if (type === "multifamily") {
      return t("Units");
    }
    if (type === "plot") {
      return t("Index");
    }
    // For apartment, house/villa, and others
    return t("bedrooms");
  };

  const capitalizeFirstLetter = (string) => {
    if (!string) return "";
    return string.charAt(0).toUpperCase() + string.slice(1);
  };

  // Helper function to format CHF prices with comma thousand separators
  const formatWithApostrophe = (value) => {
    if (value === null || value === undefined || value === "") return "";
    const numValue =
      typeof value === "string"
        ? Number(value.replace(/[' ,]/g, ""))
        : Number(value);
    if (Number.isNaN(numValue)) return value;
    
    // Format with Swiss CHF format (apostrophe as thousand separator)
    const formatted = numValue.toFixed(2);
    const parts = formatted.split('.');
    const integerPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, "'");
    return `${integerPart}.${parts[1]}`;
  };

  useEffect(() => {
    // We only want to fetch data if there's a defined flow
    if (!currentFlow) {
      setIsLoading(false);
      setData([]);
      setDisplayedOffers([]);
      return;
    }

    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      let endpoint = "";
      if (currentFlow === "offer") {
        endpoint = `${BASE_URL}/property/latest/offers?limit=30`;
      } else if (currentFlow === "demand") {
        endpoint = `${BASE_URL}/property/latest/demands?limit=30`;
      } else {
        // Should not happen, but as a safeguard
        setIsLoading(false);
        return;
      }

      try {
        const response = await authFetch(endpoint);
        if (!response.ok) {
          throw new Error(`Failed to fetch ${currentFlow}s`);
        }

        const contentType = response.headers.get("content-type");
        if (contentType && contentType.indexOf("application/json") !== -1) {
          const fetchedData = await response.json();

          // inside fetchData(), after fetchedData is parsed and BEFORE finally:
          if (currentFlow === "offer") {
            setDisplayedOffers(
              Array.isArray(fetchedData) ? [...fetchedData] : [],
            );
            setData([]);
            setOfferSliderVersion((v) => v + 1); // <- force remount
          } else {
            setData(Array.isArray(fetchedData) ? [...fetchedData] : []);
            setDisplayedOffers([]);
            setDemandSliderVersion((v) => v + 1); // <- force remount
          }
        } else {
          const text = await response.text();
          throw new Error(
            `Server returned a non-JSON response. Check your API route. Response was: "${text.substring(
              0,
              50,
            )}..."`,
          );
        }
      } catch (err) {
        setError(err.message);
        console.error(`Error fetching ${currentFlow}s:`, err);
      } finally {
        setIsLoading(false);
      }
    };

    // Initial fetch
    fetchData();

    // Set up the interval for continuous fetching
    // const intervalId = setInterval(fetchData, 60000);

    // Cleanup function to clear the interval when the component unmounts
    // or when the dependencies (currentFlow) change
    return () => {
      // Polling disabled to prevent property changes during animation
      // clearInterval(intervalId);
    };
  }, [currentFlow]);

  useEffect(() => {
    const { fromMyMatchingsTable, fromDiscoverMore, fromBookmarksPage } =
      location.state || {};
    if (fromMyMatchingsTable || fromDiscoverMore || fromBookmarksPage) {
      setShowEditButton(false);
    } else {
      setShowEditButton(true);
    }
    const fetchPropertyDetails = async () => {
      if (!id) {
        setError("No property ID provided.");
        setLoading(false);
        return;
      }

      setLoading(true);
      setProperty(null);
      setError(null);

      try {
        const response = await authFetch(`${BASE_URL}/property/${id}`);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        setProperty(data.data);
        setActiveThumb(0);
        console.log("Fetched property data:", data.data);
        const showcase = data.data?.PropertyShowcase;
        setVendorDetails({
          name: showcase?.vendorNameVisible ? showcase.vendorName : "",
          email: showcase?.emailVisible ? showcase.email : "",
          phone: showcase?.phoneVisible ? showcase.phone : "",
        });
      } catch (err) {
        console.error(`Failed to fetch property ${id}:`, err);
        setError("Failed to load property details. Please try again.");
        toast.error("Failed to load property details.");
      } finally {
        setLoading(false);
      }
    };

    // Fetch details only if allPropertyIds are loaded or if it's the initial load
    if (allPropertyIds.length > 0 || currentIndex === -1) {
      fetchPropertyDetails();
    }
  }, [id, allPropertyIds, location.state]);

  // Function to refresh property data after image upload
  const refreshPropertyData = useCallback(async () => {
    if (!id) return;

    try {
      const response = await authFetch(`${BASE_URL}/property/${id}`);
      if (response.ok) {
        const data = await response.json();
        setProperty(data.data);
        setActiveThumb(0);
        const showcase = data.data?.PropertyShowcase;
        setVendorDetails({
          name: showcase?.vendorNameVisible ? showcase.vendorName : "",
          email: showcase?.emailVisible ? showcase.email : "",
          phone: showcase?.phoneVisible ? showcase.phone : "",
        });
      }
    } catch (err) {
      console.error("Failed to refresh property data:", err);
    }
  }, [id]);

  const handleEditProperty = useCallback(() => {
    if (!id) {
      toast.error("Cannot edit: Property ID is missing.");
      return;
    }

    // Logic to navigate based on currentFlow
    if (currentFlow === "offer") {
      navigate(`/OfferProperty/${id}`);
      console.log(`Navigating to edit offer property with ID: ${id}`);
    } else if (currentFlow === "demand") {
      navigate(`/SearchProperty/${id}`); // Assuming 'SearchProperty' is your demand edit page
      console.log(`Navigating to edit demand property with ID: ${id}`);
    } else {
      // Fallback for unexpected currentFlow values
      toast.error("Cannot navigate to edit page.");
      console.warn(`Attempted to edit with unknown flow: ${currentFlow}`);
    }
  }, [id, navigate, currentFlow]); // Ensure currentFlow is in the dependency array

  const handlePreviousProperty = useCallback(() => {
    navigate(-1);
  }, [navigate]);
  // Force Slider to fully re-mount whenever the demand ids change
  const sliderKey = React.useMemo(() => {
    return Array.isArray(data) && data.length
      ? data.map((d) => d.id).join("-")
      : "empty";
  }, [data]);

  // Force Slider to re-mount whenever offer IDs change
  const offersSliderKey = React.useMemo(() => {
    return Array.isArray(displayedOffers) &&
      displayedOffers.length
      ? displayedOffers.map((o) => o.id).join("-")
      : "empty";
  }, [displayedOffers]);

  // Get images for display (either from property or static fallback)
  const displayImages = (() => {
    const showcase = property?.PropertyShowcase;
    const images = [];

    if (showcase?.mainImage) {
      images.push(showcase.mainImage);
    }

    if (showcase?.images && showcase.images.length > 0) {
      const filteredOtherImages = showcase.images.filter(
        (img) => img !== showcase.mainImage,
      );
      images.push(...filteredOtherImages);
    }

    return images;
  })();

  // Authorization: must be logged in; must be owner OR arrived via a legitimate navigation (matching/bookmarks/discover)
  useEffect(() => {
    if (loadingAuth || loading) return;

    if (!isAuthenticated) {
      navigate("/auth/login", { replace: true });
      return;
    }

    if (property && currentUser?.id) {
      const isOwner = property.userId === currentUser.id;
      const hasLegitimateAccess = fromMyMatchingsTable || fromDiscoverMore || fromBookmarksPage;
      if (!isOwner && !hasLegitimateAccess) {
        toast.error(
          t("access_denied") ||
            "Access denied. You don't have permission to view this property."
        );
        navigate("/", { replace: true });
      }
    }
  }, [loadingAuth, loading, isAuthenticated, property, currentUser, fromMyMatchingsTable, fromDiscoverMore, fromBookmarksPage, navigate, t]);

  // Fetch criteria groups for translating identifiers to display values
  useEffect(() => {
    const fetchCriteriaGroups = async () => {
      try {
        const currentLanguage = i18n.language === "en-US" ? "en" : i18n.language;
        const response = await authFetch(
          `${BASE_URL}/property/criteria-options/${currentLanguage}`
        );
        const result = await response.json();
        if (response.ok && result.data) {
          setCriteriaGroups(result.data);
        }
      } catch (error) {
        console.error("Error fetching criteria groups:", error);
      }
    };
    fetchCriteriaGroups();
  }, [i18n.language]);

  // Helper function to convert identifier to display value
  const getDisplayValueFromIdentifier = (groupIdentifier, valueIdentifier) => {
    const group = criteriaGroups.find(g => g.identifier === groupIdentifier);
    if (!group) {
      return valueIdentifier;
    }

    const option = group.values?.find(v =>
      (typeof v === 'object' && v.identifier === valueIdentifier)
    );

    return option ? (typeof option === 'object' ? option.value : option) : valueIdentifier;
  };

  if (loadingAuth || loading || (currentIndex === -1 && id && allPropertyIds.length > 0)) {
    return <div className={styles.message}>Loading property details...</div>;
  }

  if (error) {
    return <div className={styles.message}>{error}</div>;
  }

  if (!property) {
    return (
      <div className={styles.message}>
        Property not found. It might have been deleted or never existed.
      </div>
    );
  }

  // Block render if unauthorized (prevent flash of content while navigating away)
  if (!isAuthenticated) return null;
  if (property && currentUser?.id && property.userId !== currentUser.id && !fromMyMatchingsTable && !fromDiscoverMore && !fromBookmarksPage && !fromNewOffers) {
    return null;
  }

  const isFirstPropertyInList = currentIndex === 0;
  const isLastPropertyInList = currentIndex === allPropertyIds.length - 1;
  const hasMultipleProperties = allPropertyIds.length > 1;
  const showPreviousBtn = hasMultipleProperties && !isFirstPropertyInList;
  const showNextBtn = hasMultipleProperties && !isLastPropertyInList;
  const { criteria } = property?.PropertyCriterium || {};

  const { PropertyShowcase } = property || {};
  const planLink = PropertyShowcase?.architecturePdf?.[0] || "";
  const videoLink = PropertyShowcase?.externalLink?.[0] || "";
  const webLink = PropertyShowcase?.websiteUrl || "";
  const vTourLink = PropertyShowcase?.virtualTour || "";
  const brochureLink = PropertyShowcase?.brochure?.[0] || "";

  const {
    name: vendorName,
    email: vendorEmail,
    phone: vendorPhone,
  } = vendorDetails;

  const getIconProps = (link, IconComponent, altText) => {
    const hasLink = !!link;
    const cursorStyle = hasLink ? "pointer" : "default";
    const onClickHandler = hasLink
      ? () => window.open(link, "_blank")
      : undefined;

    const unfilledColor = "#d3d3d3"; // Light gray when no link
    const filledColor = "rgba(41, 177, 133, 1)"; // Green color when link exists

    const fillColor = hasLink ? filledColor : unfilledColor;
    const textColor = hasLink ? filledColor : unfilledColor; // Green when link exists, light gray otherwise

    return {
      IconComponent: IconComponent, // The actual SVG React Component
      alt: altText, // Still useful for accessibility, passed to the component directly
      cursor: cursorStyle,
      onClick: onClickHandler,
      fillColor: fillColor, // The color to apply to the SVG's fill property
      textColor: textColor, // The color to apply to the text
    };
  };

  return (
    <div className={styles.container}>
      {currentFlow === "demand" && (
        <div className={styles.leftPanel}>
          {!isLoading && !error && data.length > 0 && (
            <div className={styles.newSearchesHeader}>
              <h2 className={styles.newSearchesTitle}>{t("new_demands")}</h2>
            </div>
          )}
          {data.length > 0 && (
            <div className={styles.propertyList}>
              <NewOffersSwiper items={data} className={styles.mySwiper} />
            </div>
          )}
        </div>
      )}

      {currentFlow === "offer" && (
        <div className={styles.leftPanel}>
          {!isLoading && !error && displayedOffers.length > 0 && (
            <div className={styles.newSearchesHeader}>
              <h2 className={styles.newSearchesTitle}>{t("new_offers")}</h2>
            </div>
          )}

          {displayedOffers.length > 0 && (
            <div className={styles.propertyList}>
              <NewSearchesSwiper items={displayedOffers} className={styles.mySwiper} />
            </div>
          )}
        </div>
      )}

      {/* Right Panel - Property Details (View Mode Only) */}
      <div className={styles.rightPanel}>
        <div className={styles.rightPanelContainer}>
          <div className={styles.imageGalleryWrapper}>
            {/* Main Image with Carousel Controls */}
            <div className={styles.mainImage} style={{ position: "relative" }}>
              {displayImages.length > 0 ? (
                <img
                  src={displayImages[activeThumb]}
                  alt="Main Property"
                  className={styles.mainImageFit}
                />
              ) : (
                <div className={styles.noImageContainer}>
                  {currentFlow === "offer" ? (
                    <button
                      className={styles.uploadImageButton}
                      onClick={() => setShowImageUploadModal(true)}
                    >
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        style={{ marginRight: "8px" }}
                      >
                        <path
                          d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15"
                          stroke="white"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M7 10L12 5L17 10"
                          stroke="white"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M12 5V15"
                          stroke="white"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      {t("upload_images") || "Upload Images"}
                    </button>
                  ) : (
                    <span>{t("No_Image")}</span>
                  )}
                </div>
              )}

              {/* --- Carousel Navigation and Dots (Added Here) --- */}
              {displayImages.length > 1 && (
                <>
                  {/* Left Arrow Button */}
                  <button
                    className={`${styles.navButton} ${styles.navButtonLeft}`}
                    onClick={() =>
                      setActiveThumb(
                        (activeThumb - 1 + displayImages.length) %
                          displayImages.length,
                      )
                    }
                  >
                    <img
                      src={ChevronLeftIcon}
                      alt="Previous"
                      className={styles.navIcon}
                    />
                  </button>

                  {/* Right Arrow Button */}
                  <button
                    className={`${styles.navButton} ${styles.navButtonRight}`}
                    onClick={() =>
                      setActiveThumb((activeThumb + 1) % displayImages.length)
                    }
                  >
                    <img
                      src={ChevronRightIcon}
                      alt="Next"
                      className={styles.navIcon}
                    />
                  </button>

                  {/* Dot Indicators */}
                  <div className={styles.dotIndicatorContainer}>
                    {displayImages.map((_, index) => (
                      <div
                        key={index}
                        className={`${styles.dotIndicator} ${
                          index === activeThumb ? styles.activeDot : ""
                        }`}
                        onClick={() => setActiveThumb(index)}
                      />
                    ))}
                  </div>
                </>
              )}
              {/* --- End of Carousel Controls --- */}
            </div>
            {/* Property Details Display */}
            <>
              {/* Info Row - Dynamic Data */}
              <div className={styles.infoRow2}>
                <div className={styles.infoRowItem}>{t("zip")}</div>
                <div className={styles.infoRowItem}>{t("city")}</div>
                <div className={styles.infoRowItem}>{t("type")}</div>
                <div className={styles.infoRowItem}>{t("Sqm")}</div>
                <div className={styles.infoRowItem}>{getRoomLabel(property?.propertyType)}</div>
                <div className={styles.infoRowItem}>{t("price")}</div>
              </div>
              <div className={styles.infoRow}>
                <div className={styles.infoRowItem}>
                  {property.PropertyAddresses[0]?.zipCode || "N/A"}
                </div>
                <div className={styles.infoRowItem}>
                  {property.PropertyAddresses[0]?.city || "N/A"}
                </div>
                <div className={styles.infoRowItem}>
                  {property.propertyType?.toUpperCase() || "N/A"}
                </div>
                <div className={styles.infoRowItem}>
                  {property.squareMeters || "N/A"}
                </div>
                <div className={styles.infoRowItem}>
                  {property.bedrooms || property.units || "N/A"}
                </div>
                <div className={styles.infoRowItem}>
                  {property.price
                    ? `${formatWithApostrophe(property.price)} ${property.currency || "CHF"}`
                    : "N/A"}
                </div>
              </div>

              {/* Icon Row (unchanged - static for now) */}
              <div className={styles.iconRow}>
                {/* Plan Icon */}
                {(() => {
                  const props = getIconProps(
                    planLink,
                    DocumentIconComponent,
                    "Plan",
                  );
                  return (
                    <div
                      className={styles.iconContainer} // New class for flex, align, gap, cursor
                      onClick={props.onClick}
                      style={{ cursor: props.cursor }}
                    >
                      <props.IconComponent
                        style={{ fill: props.fillColor }}
                        className={styles.iconImage}
                        aria-label={props.alt}
                      />
                      <span style={{ color: props.textColor }}>
                        {t("Plans")}
                      </span>
                    </div>
                  );
                })()}

                {/* Web Icon */}
                {(() => {
                  const props = getIconProps(
                    webLink,
                    GlobalIconComponent,
                    "Web",
                  );
                  return (
                    <div
                      className={styles.iconContainer} // New class for flex, align, gap, cursor
                      onClick={props.onClick}
                      style={{ cursor: props.cursor }}
                    >
                      <props.IconComponent
                        style={{ fill: props.fillColor }}
                        className={styles.iconImage}
                        aria-label={props.alt}
                      />
                      <span style={{ color: props.textColor }}>{t("Web")}</span>
                    </div>
                  );
                })()}

                {/* V-Tour Icon */}
                {(() => {
                  const props = getIconProps(
                    vTourLink,
                    VirtualTourComponent,
                    "V-Tour",
                  );
                  return (
                    <div
                      className={styles.iconContainer} // New class for flex, align, gap, cursor
                      onClick={props.onClick}
                      style={{ cursor: props.cursor }}
                    >
                      <props.IconComponent
                        style={{ fill: props.fillColor }}
                        className={styles.iconImage}
                        aria-label={props.alt}
                      />
                      <span style={{ color: props.textColor }}>V-tour</span>
                    </div>
                  );
                })()}

                {/* Brochure Icon */}
                {(() => {
                  const props = getIconProps(
                    brochureLink,
                    DocumentIconComponent,
                    "Brochure",
                  );
                  return (
                    <div
                      className={styles.iconContainer} // New class for flex, align, gap, cursor
                      onClick={props.onClick}
                      style={{ cursor: props.cursor }}
                    >
                      <props.IconComponent
                        style={{ fill: props.fillColor }}
                        className={styles.iconImage}
                        aria-label={props.alt}
                      />
                      <span style={{ color: props.textColor }}>
                        {t("Brochure")}
                      </span>
                    </div>
                  );
                })()}
              </div>

              {/* Warning message when all icons are empty - only for search users */}
              {currentFlow === "demand" && !planLink && !webLink && !vTourLink && !brochureLink && (
                <div className={styles.iconWarningMessage}>
                  {t("details_not_provided_by_seller")}
                </div>
              )}

              {/* Divider */}
              <div className={styles.divider}></div>

              {/* Property Title and Description Section */}
              {(property?.propertyTitle || property?.description) && (
                <div className={styles.criteriaSection}>
                  {property?.propertyTitle && (
                    <div className={styles.propertyInfoItem}>
                      <div className={styles.propertyTitleRow}>
                        <div>
                          <h3 className={styles.criteriaHeading}>
                            {t("property_title")}
                          </h3>
                          <p className={styles.propertyInfoValue}>
                            {property.propertyTitle}
                          </p>
                        </div>
                        {vendorDetails.name && (
                          <div className={styles.vendorNameCorner}>
                            <span className={styles.vendorLabel}>
                              {t("vendor_name_label")}
                            </span>
                            <span className={styles.vendorNameText}>
                              {vendorDetails.name}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  {property?.description && (
                    <div className={styles.propertyInfoItem}>
                      <h3 className={styles.criteriaHeading}>
                        {t("description_label")}
                      </h3>
                      <p className={styles.propertyInfoValue}>
                        {property.description}
                      </p>
                    </div>
                  )}
                </div>
              )}

              <div className={styles.criteriaSection}>
                <h3 className={styles.criteriaHeading}>{t("criteria")}:</h3>
                <div className={styles.criteriaItem}>
                  {criteriaGroups.length === 0 ? (
                    <span className={styles.criteriaValue}>Loading criteria...</span>
                  ) : criteria && Object.keys(criteria).length > 0 ? (
                    Object.entries(criteria).flatMap(([groupIdentifier, value]) => {
                      const items = Array.isArray(value) ? value : (value ? [String(value)] : []);
                      return items.map((item, idx) => {
                        // Convert identifier to display value using criteriaGroups
                        const displayValue = getDisplayValueFromIdentifier(groupIdentifier, item);
                        return (
                          <span key={`${groupIdentifier}-${idx}`} className={styles.criteriaValue}>
                            {displayValue},
                          </span>
                        );
                      });
                    })
                  ) : (
                    <span className={styles.criteriaValue}>
                      {t("No specific criteria available.")}
                    </span>
                  )}
                </div>
              </div>
              {/* <div className={styles.leftAlignWrapper}>
                <h3 className={styles.criteriaHeading}>
                  {t("vendor_of_property")}:
                </h3>
              </div> */}

              {(vendorDetails.name ||
                vendorDetails.email ||
                vendorDetails.phone) && (
                <div className={styles.vendorInfoContainer}>
                  {/* {vendorDetails.name && (
                    <span className={styles.vendorName}>
                      {vendorDetails.name}
                    </span>
                  )} */}
                  {/* These were commented out in your original JSX, so keeping them commented */}
                  {/* <div className={styles.vendorContact}>
                                        {vendorDetails.email && (
                                            <span>{vendorDetails.email}</span>
                                        )}
                                        {vendorDetails.phone && (
                                            <span>{vendorDetails.phone}</span>
                                        )}
                                    </div> */}
                </div>
              )}

              {/* Divider */}
              <div className={styles.divider}></div>

              {/* Image Upload Modal */}
              {currentFlow === "offer" && (
                <PropertyImageUploadModal
                  isOpen={showImageUploadModal}
                  onClose={() => setShowImageUploadModal(false)}
                  propertyId={id}
                  onSuccess={refreshPropertyData}
                />
              )}

              {/* Bottom Navigation with Prev/Next buttons */}
              <div className={styles.bottomNav}>
                <div className={styles.bottomNavContent}>
                  <button
                    onClick={handlePreviousProperty}
                    className={styles.navButton2}
                  >
                    {t("Back")}
                  </button>
                  {currentFlow === "demand" && (
                    <div className={styles.matchingSuccessMessage}>
                      {t("matching_success_message")}
                    </div>
                  )}
                </div>

                {/* These were commented out in your original JSX, so keeping them commented */}
                {/* <button className={styles.bottomNavBtnGreen}>
                                    <span style={{ fontSize: 20 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                            <img src={mobileIcon} alt="call" className={styles.iconImage} />
                                        </div>
                                    </span>
                                    Call
                                </button> */}
                {/* <button className={styles.bottomNavBtnGreen}>
                                    <span style={{ fontSize: 20 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                            <img src={emailIcon} alt="mail" className={styles.iconImage} />
                                        </div>
                                    </span>
                                    Email
                                </button> */}
                {/* <button className={styles.bottomNavBtnGreen}>
                                    <span style={{ fontSize: 20 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                            <img src={chatIcon} alt="sms" className={styles.iconImage} />
                                        </div>
                                    </span>
                                    SMS
                                </button> */}
                {showEditButton && (
                  <button
                    onClick={handleEditProperty}
                    className={styles.navButton2}
                  >
                    {t("edit")}
                  </button>
                )}
              </div>
            </>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditOrView;
